import type { StoryBlankSpec } from '../data/stories/types';

const API_BASE = import.meta.env.VITE_API_URL ?? '';

export type Story2ApiMode = 'live' | 'mock';

export interface Story2BeatResponse {
  template: string;
  blanks?: Record<string, StoryBlankSpec>;
}

export interface Story2StepResult {
  beat?: Story2BeatResponse;
  done: boolean;
  mode: Story2ApiMode;
}

export async function fetchStory2Health(): Promise<Story2ApiMode | null> {
  try {
    const res = await fetch(`${API_BASE}/api/health`, {
      credentials: 'include',
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { story2?: Story2ApiMode };
    return data.story2 === 'live' || data.story2 === 'mock' ? data.story2 : null;
  } catch {
    return null;
  }
}

const STORY2_FETCH_MS = 35_000;

export async function fetchStory2Step(body: {
  action: 'start' | 'continue';
  seedId: string;
  unlockedKeys: string[];
  slots: Record<string, string>;
  completedLines: string[];
}): Promise<Story2StepResult> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), STORY2_FETCH_MS);

  let res: Response;
  try {
    res = await fetch(`${API_BASE}/api/story2/step`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
  } catch (e) {
    clearTimeout(timer);
    if (e instanceof Error && e.name === 'AbortError') {
      throw new Error(
        'Historien tog for lang tid — tjek forbindelsen og prøv igen.',
      );
    }
    throw new Error(
      'Kunne ikke kontakte serveren. Kør med VITE_USE_API=true og start server.',
    );
  } finally {
    clearTimeout(timer);
  }

  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(
      err.error ??
        (res.status === 404
          ? 'Serveren kender ikke Fortælling 2 — start server og byg igen.'
          : 'Kunne ikke hente næste linje. Tjek at serveren kører.'),
    );
  }

  return (await res.json()) as Story2StepResult;
}
